import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  CheckBox,
  NumberBox,
  Popover,
  SelectBox,
  Tabs,
  TagBox,
  TextArea,
  TextBox,
  Validator,
} from "devextreme-react";
import CustomStore from "devextreme/data/custom_store";

import {
  AsyncRule,
  PatternRule,
  RequiredRule,
  StringLengthRule,
  CustomRule,
  RangeRule,
} from "devextreme-react/validator";
import { LookupsType } from "src/views/PlannedRunsCalendar/AddRunForm";
import {
  newRun,
  PlannedRunDetails,
} from "src/views/PlannedRunsCalendar/constants";
import { useAuth0 } from "src/react-auth0-spa";

const labelStyle = {
  width: 220,
  marginTop: 3,
  paddingLeft: 20,
  color: "#909090",
};
const labelStyleAnalysis = { ...labelStyle, width: "300px" };
const itemStyle = {
  minWidth: 230,
  marginTop: 3,
  width: "100%",
  paddingRight: 20,
};
const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  alignSelf: "start",
  width: "100%",
};

const logLevels = ["Debug", "Fatal", "Info", "Warnings"];

const saveIntensitiesOptions = ["none", "some", "all"];

const runLengths = [
  { name: "Half-day", value: 6 },
  { name: "Day", value: 24 },
  { name: "Two-day", value: 48 },
];

const prepMethods = ["adept", "elevate", "third party"];

const readOrders = ["I1,I2,R1,R2", "R1,I1,I2,R2", "R1,R2,I1,I2", "R1,I1,R2,I2"];

const chemistryVersions = [
  "Cloudbreak",
  "CloudbreakFS",
  "CloudbreakUQ",
  "Trinity",
];

const kitTypes = [
  "OneHundredFiftyCycles",
  "ThreeHundredCycles",
  "SixHundredCycles",
];

const kitSizes = ["150Cycles", "300Cycles", "600Cycles"];

const libraryTypes = ["Circular", "Linear"];

const lowDiversityOptions = [
  { name: "No (use defualt PMG Mask)", value: "" },
  { name: "Yes (set PMG mask to I1:Y4N*)", value: "I1:Y4N*" },
];

// const polonyDensityOptions = [
//   { name: "Standard Cloudbreak (0.5)", value: 0.5 },
//   { name: "Standard CloudbreakFS (0.45)", value: 0.45 },
//   { name: "High Density (0.3)", value: 0.3 },
// ];

const targetedReads = ["High", "Med", "Low"];

const availableSequencingPanelIDs = [
  "Other",
  "TWIST-Trinity-Exome-Workflow",
  "IDT-xGen-Exome-Kit",
];

const NarrowWindowThreshold = 1050;

export const recipeBranchStore = new CustomStore({
  key: "name",
  useDefaultSearch: true,
  load: (loadOptions) => {
    console.log("loadOptions", loadOptions);
    let data = [...(window as any).recipeBranches];

    // Simple filter logic: DevExtreme provides a [field, operation, value] structure
    if (Array.isArray(loadOptions.filter) && loadOptions.filter.length > 0) {
      const [field, operation, value] = loadOptions.filter[0];
      console.log("filter", { field, operation, value });
      if (operation === "contains" || operation === "startswith") {
        const lcValue = String(value).toLowerCase();
        data = data.filter((item) =>
          operation === "contains"
            ? item[field].toLowerCase().includes(lcValue)
            : item[field].toLowerCase().startsWith(lcValue)
        );
      }
    }

    const skip = loadOptions.skip ?? 0;
    const take = 50; // loadOptions.take ?? 20;

    return Promise.resolve({
      data: data.slice(skip, skip + take),
      totalCount: data.length,
    });
  },
});

const librariesStore = new CustomStore({
  key: "name",
  useDefaultSearch: true,
  load: (loadOptions) => {
    console.log("loadOptions", loadOptions);
    let data = [...(window as any).libraries.map((v) => ({ name: v.name }))];

    console.log("library data", data);

    // Simple filter logic: DevExtreme provides a [field, operation, value] structure
    if (Array.isArray(loadOptions.filter) && loadOptions.filter.length > 0) {
      const [field, operation, value] = loadOptions.filter[0];
      console.log("filter", { field, operation, value });
      if (operation === "contains" || operation === "startswith") {
        const lcValue = String(value).toLowerCase();
        data = data.filter((item) =>
          operation === "contains"
            ? item[field].toLowerCase().includes(lcValue)
            : item[field].toLowerCase().startsWith(lcValue)
        );
      }
    }

    const skip = loadOptions.skip ?? 0;
    const take = 50; // loadOptions.take ?? 20;

    return Promise.resolve({
      data: data.slice(skip, skip + take),
      totalCount: data.length,
    });
  },
});

function AddRunSequencingContent({
  lookups,
  data,
  setData,
  setEditedData,
  setForceRefresh,
  nameIsReadOnly,
}: {
  lookups: LookupsType;
  data: PlannedRunDetails;
  setData: React.Dispatch<React.SetStateAction<PlannedRunDetails>>;
  setEditedData: React.Dispatch<
    React.SetStateAction<Partial<PlannedRunDetails>>
  >;
  setForceRefresh: (val: boolean) => void;
  nameIsReadOnly: boolean;
}) {
  const { myFetch } = useAuth0();
  const [isNarrow, setIsNarrow] = useState(
    window.innerWidth < NarrowWindowThreshold
  );
  const [tab, setTab] = useState("Sequencing Parameters");
  const isTrinity = data?.chemistryVersion === "Trinity";

  useEffect(() => {
    const handleResize = () => {
      setIsNarrow(window.innerWidth < NarrowWindowThreshold);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setField = (key: string, value: any) => {
    setData(v => ({ ...v, [key]: value }));
    setEditedData(v => ({ ...v, [key]: value }));
  };

  // when dualLibrary toggles, normalize lane-2
  useEffect(() => {
    if (!isTrinity) return;
    if (data?.dualLibrary) {
      // init lane 2 to lane 1 if empty (same panel allowed)
      if (!data?.sequencingPanel2ID) {
        setField("sequencingPanel2ID", data?.sequencingPanelID ?? "");
      }
    } else {
      // single-lane → clear lane 2
      if (data?.sequencingPanel2ID) {
        setField("sequencingPanel2ID", "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrinity, data?.dualLibrary]);

  const validateManifest = useCallback(
    (params) =>
      new Promise<void>((resolve, reject) => {
        const pick = ({
          runName,
          manifest,
          i1Cycles,
          i2Cycles,
          r1Cycles,
          r2Cycles,
          tileSelectionModule,
          preparationMethod,
          chemistryVersion,
          targetedReads,
          kitConfiguration,
          kitType,
          wellLayout,
        }) => ({
          runName,
          manifest,
          i1Cycles,
          i2Cycles,
          r1Cycles,
          r2Cycles,
          tileSelectionModule,
          chemistryVersion,
          targetedReads,
          preparationMethod:
            preparationMethod === "third party"
              ? "ThirdParty"
              : preparationMethod,
          kitConfiguration,
          kitType,
          wellLayout,
        });
        console.log("data", data);
        const tdata = pick(data);
        tdata.manifest = params.value;

        if (/\r\n/g.test(params.value)) {
          reject("Manifest cannot contain CRLF characters");
        }

        const payload = pick(tdata);

        const mappedKitConfig = kitSizes[kitTypes.indexOf(payload.kitType)];

        const newpayload = {
          ...payload,
          kitSize: mappedKitConfig,
          kitConfiguration: mappedKitConfig,
        };

        console.log("manifest verify payload", newpayload);
        myFetch("/manifest", {
          method: "POST",
          body: JSON.stringify({
            ...newpayload,
            kitSize: mappedKitConfig,
            kitConfiguration: mappedKitConfig,
          }),
        })
          .then((response) => response.json())
          .then((resp) => {
            if (!resp.isValid) reject("Manifest: " + resp.message);
            else resolve();
          })
          .catch((error) => {
            reject("Cannot validate manifest: " + JSON.stringify(error));
          });
      }),
    [data, myFetch]
  );

  const handleManifestSelected = useCallback(
    (e) => {
      e.preventDefault();
      let reader = new FileReader();
      let file = e.target.files[0];
      reader.onloadend = () => {
        let manifestStr = String(reader.result);
        // replace any CRLF characters in the file with just LF
        manifestStr = manifestStr.replace(/\r\n/g, "\n");
        setData((v) => {
          return { ...v, manifest: manifestStr };
        });
        setEditedData((v) => {
          return { ...v, manifest: manifestStr };
        });
      };
      reader.readAsText(file);
    },
    [setData, setEditedData]
  );

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingBottom: "20px",
          paddingTop: "20px",
        }}
      >
        <Tabs
          items={["Sequencing Parameters", "Manifest", "Analysis Parameters"]}
          style={{ fontSize: "12px", width: "500px" }}
          selectedItem={tab}
          onSelectedItemChange={(e) => {
            setTab(e);
          }}
        />
      </div>
      {/* Sequencing Parameters */}
      <div
        style={
          isNarrow
            ? {
                display: tab === "Sequencing Parameters" ? "flex" : "none",
                flexDirection: "column",
                gap: "2px",
                height: "calc(100% - 150px)",
                overflow: "auto",
                paddingBottom: "20px",
                backgroundColor: "#fBfBfB",
              }
            : {
                display: tab === "Sequencing Parameters" ? "grid" : "none",
                gridAutoFlow: "column",
                gridTemplateRows: "repeat(auto-fill, minmax(20px, 2fr))",
                gap: "8px",
                height: "calc(100% - 150px)",
                overflowY: "auto",
                paddingBottom: "20px",
                backgroundColor: "#fBfBfB",
              }
        }
      >
        <div style={rowStyle}>
          <div style={labelStyle}>Group ID *</div>
          <SelectBox
            displayExpr="name"
            style={itemStyle}
            searchMode="startswith"
            searchExpr="name"
            searchEnabled
            showClearButton
            valueExpr="id"
            items={lookups.groups}
            readOnly={nameIsReadOnly}
            value={data.groupID}
            onValueChanged={(e) => {
              const groupID = e.value;
              if (groupID === 0) {
                setData((d) => {
                  return { ...d, groupID, runCountNumber: 0, runName: "" };
                });
                setEditedData((d) => {
                  return { ...d, groupID, runCountNumber: 0, runName: "" };
                });
              } else {
                const grp = lookups.groups.find((v) => v.id === e.value);
                if (grp == null) return;
                const rname =
                  grp.abbr +
                  "-" +
                  (grp.runCount + 1).toString().padStart(4, "0");
                setData((d) => {
                  return {
                    ...d,
                    groupID,
                    runCountNumber: grp.runCount + 1,
                    runName: rname,
                  };
                });
                setEditedData((d) => {
                  return {
                    ...d,
                    groupID,
                    runCountNumber: grp.runCount + 1,
                    runName: rname,
                  };
                });
              }
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Run Name *</div>
          <TextBox
            style={itemStyle}
            value={data?.runName}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, runName: e.value };
              });
              setEditedData((v) => {
                return { ...v, runName: e.value };
              });
            }}
            readOnly={data && data.groupID !== 0}
          >
            <Validator>
              <RequiredRule message="Run Name is required" />{" "}
            </Validator>
          </TextBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Run Length *</div>
          <SelectBox
            style={itemStyle}
            items={runLengths}
            displayExpr="name"
            valueExpr={"value"}
            value={data?.runLength}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, runLength: e.value };
              });
              setEditedData((v) => {
                return { ...v, runLength: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Recipe Length is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Requester *</div>
          <SelectBox
            style={itemStyle}
            items={lookups.emails}
            searchMode="startswith"
            searchEnabled
            showClearButton
            value={data?.requester}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, requester: e.value, operator: e.value };
              });
              setEditedData((v) => {
                return { ...v, requester: e.value, operator: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Requester is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Project *</div>
          <SelectBox
            style={itemStyle}
            items={lookups.projects}
            searchMode="startswith"
            searchEnabled
            showClearButton
            value={data?.project}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, project: e.value };
              });
              setEditedData((v) => {
                return { ...v, project: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Project is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Tile Selection Module *</div>
          <SelectBox
            style={itemStyle}
            items={lookups.tileSelectionModules}
            displayExpr="name"
            valueExpr="value"
            searchMode="startswith"
            acceptCustomValue
            onCustomItemCreating={(e) => {
              const newitem = {
                name: e.text,
                value: `tile_selections/${e.text}.txt`,
              };
              console.log("new item", newitem);
              lookups.tileSelectionModules.push(newitem);
              e.customItem = newitem;
            }}
            searchEnabled
            showClearButton
            value={data?.tileSelectionModule}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, tileSelectionModule: e.value };
              });
              setEditedData((v) => {
                return { ...v, tileSelectionModule: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Tile selection module is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Chemistry Version *</div>
          <SelectBox
            style={itemStyle}
            items={chemistryVersions}
            value={data?.chemistryVersion}
            onValueChanged={(e) => {
              const trinityEdits =
                e.value === "Trinity"
                  ? {
                      preparationMehod: "elevate",
                      libraryType: "Linear",
                      sequencingPanelID: "Other",
                    }
                  : { sequencingPanelID: "" };
              setData((v) => {
                return { ...v, chemistryVersion: e.value, ...trinityEdits };
              });
              setEditedData((v) => {
                return { ...v, chemistryVersion: e.value, ...trinityEdits };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Chemistry version is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        {isTrinity && (
          <>
            <div style={rowStyle}>
              <div style={labelStyle}>
                {data?.dualLibrary ? "Panel 1 ID *" : "Panel ID *"}
              </div>
              <SelectBox
                style={itemStyle}
                items={availableSequencingPanelIDs} // no filtering
                value={data?.sequencingPanelID ?? ""}
                onValueChanged={(e) => {
                  setField("sequencingPanelID", e.value);
                  // if dual, optionally mirror lane 2 when empty
                  if (data?.dualLibrary && !data?.sequencingPanel2ID) {
                    setField("sequencingPanel2ID", e.value);
                  }
                }}
              />
            </div>

            {data?.dualLibrary && (
              <div style={rowStyle}>
                <div style={labelStyle}>Panel 2 ID *</div>
                <SelectBox
                  style={itemStyle}
                  items={availableSequencingPanelIDs} // no filtering; same panel allowed
                  value={data?.sequencingPanel2ID ?? ""}
                  onValueChanged={(e) => setField("sequencingPanel2ID", e.value)}
                />
              </div>
            )}
          </>
        )}

        <div style={rowStyle}>
          <div style={labelStyle}>Prep Method *</div>
          <SelectBox
            style={itemStyle}
            items={prepMethods}
            value={data?.preparationMethod}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, preparationMethod: e.value };
              });
              setEditedData((v) => {
                return { ...v, preparationMethod: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Library Type *</div>
          <SelectBox
            style={itemStyle}
            items={libraryTypes}
            value={data?.libraryType}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, libraryType: e.value };
              });
              setEditedData((v) => {
                return { ...v, libraryType: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Library type is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Kit Type *</div>
          <SelectBox
            style={itemStyle}
            items={kitTypes}
            value={data?.kitType}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, kitType: e.value };
              });
              setEditedData((v) => {
                return { ...v, kitType: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Low Diversity Amplicon</div>
          <SelectBox
            style={itemStyle}
            items={lowDiversityOptions}
            displayExpr="name"
            valueExpr="value"
            value={data?.pmgMask}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, pmgMask: e.value };
              });
              setEditedData((v) => {
                return { ...v, pmgMask: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>PMGShift</div>
          <NumberBox
            style={itemStyle}
            value={data?.pmgShift}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, pmgShift: e.value };
              });
              setEditedData((v) => {
                return { ...v, pmgShift: e.value };
              });
            }}
          >
            <Validator>
              <RangeRule
                min={0}
                max={20}
                message="PMGShift must be between 0 and 20"
              />
            </Validator>
          </NumberBox>
        </div>

        <div style={rowStyle}>
          <div
            id="pmgThreshold"
            style={{ ...labelStyle, display: "flex", gap: "5px" }}
          >
            PMG Threshold{" "}
            <i
              style={{ color: "black", fontSize: "20px", marginTop: "-3px" }}
              className="dx-icon-info"
            ></i>
          </div>
          <NumberBox
            style={itemStyle}
            value={data?.pmgThreshold}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, pmgThreshold: e.value };
              });
              setEditedData((v) => {
                return { ...v, pmgThreshold: e.value };
              });
            }}
          >
            <Validator>
              <RangeRule
                min={0.1}
                max={1}
                message="PMG Threshold must be between 0.1 and 1"
              />
              <RequiredRule message="PMG Threshold is required" />
            </Validator>
          </NumberBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Read Order *</div>
          <SelectBox
            style={itemStyle}
            items={readOrders}
            value={data?.readOrder}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, readOrder: e.value };
              });
              setEditedData((v) => {
                return { ...v, readOrder: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>R1</div>
          <NumberBox
            style={itemStyle}
            value={data?.r1Cycles}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, r1Cycles: e.value };
              });
              setEditedData((v) => {
                return { ...v, r1Cycles: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>R2</div>
          <NumberBox
            style={itemStyle}
            value={data?.r2Cycles}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, r2Cycles: e.value };
              });
              setEditedData((v) => {
                return { ...v, r2Cycles: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>I1</div>
          <NumberBox
            style={itemStyle}
            value={data?.i1Cycles}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, i1Cycles: e.value };
              });
              setEditedData((v) => {
                return { ...v, i1Cycles: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>I2</div>
          <NumberBox
            style={itemStyle}
            value={data?.i2Cycles}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, i2Cycles: e.value };
              });
              setEditedData((v) => {
                return { ...v, i2Cycles: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Recipe Branch</div>

          <Autocomplete
            dataSource={recipeBranchStore}
            style={itemStyle}
            value={data.recipeBranch}
            valueExpr={"name"}
            onValueChanged={(e) => {
              console.log("newvalue", e);
              setData((v) => {
                return { ...v, recipeBranch: e.value };
              });
              setEditedData((v) => {
                return { ...v, recipeBranch: e.value };
              });
            }}
            minSearchLength={2}
            searchTimeout={500}
            placeholder="Type branch name..."
          >
            <Validator>
              <CustomRule
                message="The recipe branch must match an existing recipe branch"
                validationCallback={(e) => {
                  e.isValid = true;
                  console.log("validation callback", e);
                  if (!e.value) return true;
                  const found = (window as any).recipeBranches.find(
                    (v) => v.name === e.value
                  );
                  if (found == null) {
                    e.isValid = false;
                    return false;
                  }
                  return true;
                }}
              />
            </Validator>
          </Autocomplete>
        </div>

        {/* <div style={rowStyle}>
          <div style={labelStyle} />
          <div style={{ ...itemStyle, display: "flex", gap: "5px" }}>
            <button
              style={{ marginLeft: "auto" }}
              onClick={() => setForceRefresh(true)}
            >
              Refresh Recipes
            </button>
          </div>
        </div> */}

        <div style={rowStyle}>
          <div style={labelStyle}>Tags</div>
          <TagBox
            style={itemStyle}
            value={data?.tags}
            width={"100%"}
            items={lookups.tags}
            searchEnabled
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, tags: e.value };
              });
              setEditedData((v) => {
                return { ...v, tags: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Description</div>
          <TextBox
            style={itemStyle}
            value={data?.description}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, description: e.value };
              });
              setEditedData((v) => {
                return { ...v, description: e.value };
              });
            }}
          >
            <Validator>
              <StringLengthRule
                max={100}
                message="Description must be less than 100 characters"
              />
              <PatternRule
                pattern="^[A-Za-z0-9\-_. ]+$"
                message="Only alphanumeric characters, as well as spaces, periods and hyphens are allowed in the description"
              />
            </Validator>
          </TextBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Targeted Reads *</div>
          <SelectBox
            style={itemStyle}
            items={targetedReads}
            value={data.targetedReads}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, targetedReads: e.value };
              });
              setEditedData((v) => {
                return { ...v, targetedReads: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Targeted Reads selection is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Single Lane</div>
          <CheckBox
            style={itemStyle}
            value={data.singleLane}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, singleLane: e.value };
              });
              setEditedData((v) => {
                return { ...v, singleLane: e.value };
              });
            }}
          ></CheckBox>
        </div>

        <div style={rowStyle} />

        <div
          style={{
            ...rowStyle,
            display: "flex",
            justifyContent: "center",
            fontWeight: "bold",
            width: "100%",
          }}
        >
          Library Information
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Library ID *</div>

          <Autocomplete
            dataSource={librariesStore}
            valueExpr={"name"}
            style={itemStyle}
            value={data.libraryName}
            onValueChanged={(e) => {
              const libraryID =
                (window as any).libraries.find((v) => v.name === e.value)?.id ||
                null;
              setData((v) => {
                return { ...v, libraryName: e.value, libraryID };
              });
              setEditedData((v) => {
                return { ...v, libraryName: e.value, libraryID };
              });
            }}
            minSearchLength={1}
            searchTimeout={500}
            placeholder="Type library name..."
          >
            <Validator>
              <CustomRule
                message="The library must match an existing library"
                validationCallback={(e) => {
                  e.isValid = true;
                  console.log("validation callback", e, data);
                  if (!data.libraryID || data.libraryID <= 0) {
                    e.isValid = false;
                    return false;
                  }
                  return true;
                }}
              />
            </Validator>
          </Autocomplete>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Library Conc.</div>
          <NumberBox
            style={itemStyle}
            value={data?.libraryConcentration}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, libraryConcentration: e.value };
              });
              setEditedData((v) => {
                return { ...v, libraryConcentration: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Library Concentration is required" />{" "}
            </Validator>
          </NumberBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Spike-in ID *</div>
          <Autocomplete
            dataSource={librariesStore}
            valueExpr={"name"}
            style={itemStyle}
            value={data.spikeInLibraryName}
            onValueChanged={(e) => {
              const libraryID =
                (window as any).libraries.find((v) => v.name === e.value)?.id ||
                null;
              setData((v) => {
                return {
                  ...v,
                  spikeInLibraryName: e.value,
                  spikeInLibraryID: libraryID,
                };
              });
              setEditedData((v) => {
                return {
                  ...v,
                  spikeInLibraryName: e.value,
                  spikeInLibraryID: libraryID,
                };
              });
            }}
            minSearchLength={1}
            searchTimeout={500}
            placeholder="Type library name..."
          >
            <Validator>
              <CustomRule
                message="The library must match an existing library"
                validationCallback={(e) => {
                  e.isValid = true;
                  console.log("validation callback", e, data);
                  if (!data.spikeInLibraryID || data.spikeInLibraryID <= 0) {
                    e.isValid = false;
                    return false;
                  }
                  return true;
                }}
              />
            </Validator>
          </Autocomplete>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Spike-in Conc.</div>
          <NumberBox
            style={itemStyle}
            value={data?.spikeInLibraryConcentration}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, spikeInLibraryConcentration: e.value };
              });
              setEditedData((v) => {
                return { ...v, spikeInLibraryConcentration: e.value };
              });
            }}
          ></NumberBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Dilution Lot</div>
          <TextBox
            style={itemStyle}
            value={data?.libraryDilutionLot}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, libraryDilutionLot: e.value };
              });
              setEditedData((v) => {
                return { ...v, libraryDilutionLot: e.value };
              });
            }}
          ></TextBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Mix Stock Lot</div>
          <TextBox
            style={itemStyle}
            value={data?.libraryMixStockLot}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, libraryMixStockLot: e.value };
              });
              setEditedData((v) => {
                return { ...v, libraryMixStockLot: e.value };
              });
            }}
          ></TextBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Dual Library</div>
          <CheckBox
            style={itemStyle}
            value={data.dualLibrary}
            onValueChanged={(e) => {
              const dualLibrarySettings = e.value
                ? { l2LibraryID: undefined, l2LibraryConcentration: undefined }
                : {};
              setData((v) => {
                return { ...v, dualLibrary: e.value, ...dualLibrarySettings };
              });
              setEditedData((v) => {
                return { ...v, dualLibrary: e.value, ...dualLibrarySettings };
              });
            }}
          ></CheckBox>
        </div>

        {data.dualLibrary && (
          <>
            <div style={rowStyle}>
              <div style={labelStyle}>L2 Library ID *</div>
              <Autocomplete
                dataSource={librariesStore}
                valueExpr={"name"}
                style={itemStyle}
                value={data.l2LibraryName}
                onValueChanged={(e) => {
                  const libraryID =
                    (window as any).libraries.find((v) => v.name === e.value)
                      ?.id || null;
                  setData((v) => {
                    return {
                      ...v,
                      l2LibraryName: e.value,
                      l2LibraryID: libraryID,
                    };
                  });
                  setEditedData((v) => {
                    return {
                      ...v,
                      l2LibraryName: e.value,
                      l2LibraryID: libraryID,
                    };
                  });
                }}
                minSearchLength={1}
                searchTimeout={500}
                placeholder="Type library name..."
              >
                <Validator>
                  <CustomRule
                    message="The library must match an existing library"
                    validationCallback={(e) => {
                      e.isValid = true;
                      if (!data.l2LibraryID || data.l2LibraryID <= 0) {
                        e.isValid = false;
                        return false;
                      }
                      return true;
                    }}
                  />
                </Validator>
              </Autocomplete>
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>L2 Library Conc.</div>
              <NumberBox
                style={itemStyle}
                value={data?.l2LibraryConcentration}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, l2LibraryConcentration: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, l2LibraryConcentration: e.value };
                  });
                }}
              >
                <Validator>
                  <RequiredRule message="L2 Library Concentration is required" />{" "}
                </Validator>
              </NumberBox>
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>L2 Spike-in ID *</div>
              <Autocomplete
                dataSource={librariesStore}
                valueExpr={"name"}
                style={itemStyle}
                value={data.l2SpikeInLibraryName}
                onValueChanged={(e) => {
                  const libraryID =
                    (window as any).libraries.find((v) => v.name === e.value)
                      ?.id || null;
                  setData((v) => {
                    return {
                      ...v,
                      l2SpikeInLibraryName: e.value,
                      l2SpikeInLibraryID: libraryID,
                    };
                  });
                  setEditedData((v) => {
                    return {
                      ...v,
                      l2SpikeInLibraryName: e.value,
                      l2SpikeInLibraryID: libraryID,
                    };
                  });
                }}
                minSearchLength={1}
                searchTimeout={500}
                placeholder="Type library name..."
              >
                <Validator>
                  <CustomRule
                    message="The library must match an existing library"
                    validationCallback={(e) => {
                      e.isValid = true;
                      console.log("validation callback", e, data);
                      if (
                        !data.l2SpikeInLibraryID ||
                        data.l2SpikeInLibraryID <= 0
                      ) {
                        e.isValid = false;
                        return false;
                      }
                      return true;
                    }}
                  />
                </Validator>
              </Autocomplete>
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>L2 Spike-in Conc.</div>
              <NumberBox
                style={itemStyle}
                value={data?.l2SpikeInLibraryConcentration}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, l2SpikeInLibraryConcentration: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, l2SpikeInLibraryConcentration: e.value };
                  });
                }}
              ></NumberBox>
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>L2 Dilution Lot</div>
              <TextBox
                style={itemStyle}
                value={data?.l2LibraryDilutionLot}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, l2LibraryDilutionLot: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, l2LibraryDilutionLot: e.value };
                  });
                }}
              ></TextBox>
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>L2 Mix Stock Lot</div>
              <TextBox
                style={itemStyle}
                value={data?.l2LibraryMixStockLot}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, l2LibraryMixStockLot: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, l2LibraryMixStockLot: e.value };
                  });
                }}
              ></TextBox>
            </div>
          </>
        )}

        {/* END */}
      </div>

      {/* Manifest */}
      <div
        style={{
          display: tab === "Manifest" ? "flex" : "none",
          flexDirection: "column",
          gap: "2px",
          height: "calc(100% - 150px)",
          overflow: "auto",
          paddingBottom: "20px",
          backgroundColor: "#fBfBfB",
        }}
      >
        <TextArea
          height="90%"
          width="100%"
          spellcheck={false}
          value={data.manifest}
          onValueChanged={(e) => {
            setData((v) => {
              return { ...v, manifest: e.value };
            });
            setEditedData((v) => {
              return { ...v, manifest: e.value };
            });
          }}
        >
          <Validator>
            <AsyncRule validationCallback={validateManifest} />
          </Validator>
        </TextArea>
        <span
          style={{
            fontSize: "12px",
            color: "#909090",
            marginTop: "25px",
            marginBottom: "25px",
            paddingLeft: "20px",
            paddingRight: "20px",
          }}
        >
          I1Cycles, I2Cycles, R1Cycles, R2Cycles, KitConfiguration,
          PreparationMethod and TileSelectionModule are now ignored and will be
          overridden by the the values you select in the UI
        </span>

        <div>
          <label
            htmlFor="file-upload"
            style={{
              border: "1px solid #ccc",
              display: "inline-block",
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            Load Manifest...
          </label>
          <input
            id="file-upload"
            type="file"
            style={{ display: "none" }}
            onChange={handleManifestSelected}
          />
        </div>
      </div>

      {/* Analysis Parameters */}
      <div
        style={{
          display: tab === "Analysis Parameters" ? "flex" : "none",
          flexDirection: "column",
          gap: "20px",
          height: "calc(100% - 150px)",
          overflow: "auto",
          paddingBottom: "20px",
          backgroundColor: "#fBfBfB",
        }}
      >
        <div style={rowStyle}>
          <div style={labelStyleAnalysis}>Analysis Enabled</div>
          <CheckBox
            style={itemStyle}
            value={data.analysisEnabled}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, analysisEnabled: e.value };
              });
              setEditedData((v) => {
                return { ...v, analysisEnabled: e.value };
              });
            }}
          />
        </div>

        {data.analysisEnabled && (
          <>
            <div style={rowStyle}>
              <div style={labelStyleAnalysis}>
                Cloud Analysis Only (no OBPA)
              </div>
              <CheckBox
                style={itemStyle}
                value={data.cloudAnalysis}
                onValueChanged={(e) => {
                  const obpaROI = !e.value
                    ? { roiWidth: newRun.roiWidth, roiHeight: newRun.roiHeight }
                    : {};
                  setData((v) => {
                    return { ...v, cloudAnalysis: e.value, ...obpaROI };
                  });
                  setEditedData((v) => {
                    return { ...v, cloudAnalysis: e.value, ...obpaROI };
                  });
                }}
              />
            </div>

            {data.cloudAnalysis ? (
              <>
                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Analysis Tool Rep</div>
                  <TextBox
                    style={itemStyle}
                    value={data?.analysisToolRep}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, analysisToolRep: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, analysisToolRep: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Image Name</div>
                  <TextBox
                    style={itemStyle}
                    value={data?.imageName}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, imageName: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, imageName: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Arguments</div>
                  <TextBox
                    style={itemStyle}
                    value={data?.arguments}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, arguments: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, arguments: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>ROI Width</div>
                  <NumberBox
                    style={itemStyle}
                    value={data?.roiWidth}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, roiWidth: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, roiWidth: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>ROI Height</div>
                  <NumberBox
                    style={itemStyle}
                    value={data?.roiHeight}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, roiHeight: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, roiHeight: e.value };
                      });
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Log Level</div>
                  <SelectBox
                    style={itemStyle}
                    items={logLevels}
                    value={data.logLevel}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, logLevel: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, logLevel: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Save Intensities</div>
                  <SelectBox
                    style={itemStyle}
                    items={saveIntensitiesOptions}
                    value={data.saveIntensities}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return {
                          ...v,
                          saveIntensities: e.value,
                          uploadIntensities:
                            e.value === "some" || e.value === "all",
                        };
                      });
                      setEditedData((v) => {
                        return {
                          ...v,
                          saveIntensities: e.value,
                          uploadIntensities:
                            e.value === "some" || e.value === "all",
                        };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Seq CNNDisabled</div>
                  <CheckBox
                    style={itemStyle}
                    value={data.seqCNNDisabled}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, seqCNNDisabled: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, seqCNNDisabled: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}></div>
                  <div style={itemStyle}>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#909090",
                      }}
                    >
                      Use SeqCNNEnabled default unless checked - AOS 3.4.0 and
                      later
                    </span>
                  </div>
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Seq CNNEnabled</div>
                  <CheckBox
                    style={itemStyle}
                    value={data.seqCNNEnabled}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, seqCNNEnabled: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, seqCNNEnabled: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}></div>
                  <div style={itemStyle}>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#909090",
                      }}
                    >
                      EXPERIMENTAL ONLY - AOS 3.3.x
                    </span>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Popover
        target="#pmgThreshold"
        showEvent="mouseenter"
        hideEvent="mouseleave"
        position="top"
        width={800}
      >
        <PmgScoreTable />

      </Popover>
    </>
  );
}

const PmgScoreTable: React.FC = () => {
  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    boxShadow: "0 0 10px rgba(0,0,0,0.05)",
    backgroundColor: "white",
  };

  const thStyle: React.CSSProperties = {
    padding: "12px 16px",
    backgroundColor: "#4a90e2",
    color: "white",
    textAlign: "center",
    borderBottom: "1px solid #ddd",
  };

  const tdStyle: React.CSSProperties = {
    padding: "12px 16px",
    textAlign: "center",
    borderBottom: "1px solid #ddd",
  };

  const evenRowStyle: React.CSSProperties = {
    backgroundColor: "#f2f8fc",
  };

  const hoverRowStyle: React.CSSProperties = {
    backgroundColor: "#e0f0ff",
  };

  const rows = [
    ["Cloudbreak", "0.5", "0.3", "0.3", "N/A"],
    ["Cloudbreak UQ", "0.5", "N/A", "0.5 (no cnn)", "N/A"],
    ["Trinity", "0.45", "0.3", "0.3", "N/A"],
    ["Cloudbreak FS", "0.45", "0.3", "0.3", "N/A"],
    ["2x300 – Cloudbreak", "0.5", "0.3", "0.45 (no cnn)", "0.3"],
    ["2x300 Cloudbreak FS", "0.45", "0.3", "0.45 (no cnn)", "0.3"],
    ["Teton", "N/A", "N/A", "0.35", "N/A"],
    ["Teton Atlas", "N/A", "N/A", "0.35", "N/A"],
  ];

  return (
    <div
      style={{
        margin: "2rem",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f9f9f9",
      }}
    >
      <h6 style={{ textAlign: "center", color: "#333" }}>
        PMG Score Thresholds by Chemistry/Kit
      </h6>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Chemistry/Kits</th>
            <th style={thStyle}>Aviti PMG score threshold - Standard</th>
            <th style={thStyle}>Aviti PMG score threshold - HD mode</th>
            <th style={thStyle}>Aviti24 PMG score threshold</th>
            <th style={thStyle}>Aviti24 PMG score threshold - HD mode</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={i % 2 === 0 ? evenRowStyle : undefined}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  hoverRowStyle.backgroundColor!)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor =
                  i % 2 === 0 ? evenRowStyle.backgroundColor! : "white")
              }
            >
              {row.map((cell, j) => (
                <td key={j} style={tdStyle}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AddRunSequencingContent;
